// Replaceable notification delivery. The scheduler only talks to this interface, so a native bridge
// (Capacitor, a WidgetKit companion) or a Web Push backend can be added without touching scheduling logic.

export interface ReminderPayload {
  /** Occurrence key `scheduleId@ISO` — also used as the notification tag so the OS de-duplicates too. */
  key: string;
  scheduleId: string;
  noteId: string;
  title: string;
  body: string;
  occurrence: number;
}

export type PermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

export interface NotificationAdapter {
  readonly id: 'system' | 'in-app' | 'none';
  permission(): PermissionState;
  requestPermission(): Promise<PermissionState>;
  /** Returns true when the reminder was shown through this channel. */
  show(p: ReminderPayload): Promise<boolean>;
}

export function notificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/** iOS only exposes notifications to web apps launched from the Home Screen. */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export class SystemNotificationAdapter implements NotificationAdapter {
  readonly id = 'system' as const;

  permission(): PermissionState {
    if (!notificationsSupported()) return 'unsupported';
    return Notification.permission as PermissionState;
  }

  async requestPermission(): Promise<PermissionState> {
    if (!notificationsSupported()) return 'unsupported';
    try {
      return (await Notification.requestPermission()) as PermissionState;
    } catch {
      return 'denied';
    }
  }

  async show(p: ReminderPayload): Promise<boolean> {
    if (this.permission() !== 'granted') return false;
    const options: NotificationOptions & { renotify?: boolean; timestamp?: number } = {
      body: p.body,
      tag: p.key,
      icon: '/icons/icon-192.png',
      badge: '/icons/icon-192.png',
      data: { noteId: p.noteId, scheduleId: p.scheduleId, key: p.key, url: `/#/note/${p.noteId}` },
      timestamp: p.occurrence,
    };
    try {
      const reg = await navigator.serviceWorker?.getRegistration();
      if (reg) {
        await reg.showNotification(p.title, options);
        return true;
      }
      new Notification(p.title, options);
      return true;
    } catch {
      return false;
    }
  }
}

/** Fallback used when permission is denied or the API is missing: shows an in-app banner. */
export class InAppAdapter implements NotificationAdapter {
  readonly id = 'in-app' as const;
  constructor(private readonly present: (p: ReminderPayload) => void) {}
  permission(): PermissionState {
    return 'granted';
  }
  async requestPermission(): Promise<PermissionState> {
    return 'granted';
  }
  async show(p: ReminderPayload): Promise<boolean> {
    this.present(p);
    return true;
  }
}

/**
 * Chain: try the system channel and always also surface in-app while the app is visible,
 * so a reminder is never silently lost when the OS suppresses it.
 */
export class CompositeAdapter implements NotificationAdapter {
  readonly id = 'system' as const;
  constructor(
    private readonly system: NotificationAdapter,
    private readonly inApp: NotificationAdapter,
    private readonly isVisible: () => boolean = () => typeof document !== 'undefined' && document.visibilityState === 'visible',
  ) {}
  permission(): PermissionState {
    return this.system.permission();
  }
  requestPermission(): Promise<PermissionState> {
    return this.system.requestPermission();
  }
  async show(p: ReminderPayload): Promise<boolean> {
    const viaSystem = this.system.permission() === 'granted' ? await this.system.show(p) : false;
    if (!viaSystem || this.isVisible()) await this.inApp.show(p);
    return true;
  }
  channelFor(): 'system' | 'in-app' {
    return this.system.permission() === 'granted' ? 'system' : 'in-app';
  }
}
